import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  dashBordDetailsQuery,
  getGrafanaDataThunk,
} from "../thunk/grafanaThunk";

const currentDate = new Date();
const startDate = new Date(currentDate);
startDate.setHours(currentDate.getHours() - 1);

const endDate = new Date(currentDate);

const initialState: any = {
  chartData: {},
  loading: false,
  liveChartData: {},
  substanceTableData: {},
  componentLoading: "",
  allComponentsLoader: false,
  updateLoader: false,
  dashboardState: {},
  deviceColors: [],
  dateTime: {
    startDate,
    endDate,
  },
  fullViewChartData: {},
  userGrafanaDashboard: [],
};

const grafanaChartsSlice = createSlice({
  name: "grafana-charts",
  initialState,
  reducers: {
    addLiveChartData: (state: any, { payload }) => {
      state.liveChartData = {
        temperature: payload?.temperature?.sort(
          (a: any, b: any) => a[0] - b[0]
        ),
        humidity: payload?.humidity?.sort((a: any, b: any) => a[0] - b[0]),
        voc: payload?.voc?.sort((a: any, b: any) => a[0] - b[0]),
        pm1: payload?.pm1?.sort((a: any, b: any) => a[0] - b[0]),
        pm10: payload?.pm10?.sort((a: any, b: any) => a[0] - b[0]),
      };
    },
    handleLoading: (state: any, action: PayloadAction<any>) => {
      state.loading = action.payload;
    },
    handleComponentLoading: (state: any, { payload }) => {
      state.componentLoading = payload;
    },
    addChartReadings: (state: any, { payload }) => {
      state.chartData = {
        ...state.chartData,
        grafana_data: {
          ...state.chartData.grafana_data,
          data: [...state.chartData.grafana_data.data, payload.data],
          grafanaLayout: payload.layout,
        },
      };
    },
    updateChart: (state: any, { payload }) => {
      state.chartData = {
        ...state.chartData,
        grafana_data: payload,
      };
    },
    handleThemeChange: (state: any, { payload }) => {
      state.dashboardState = {
        ...state.dashboardState,
        dashboardData: {
          ...state?.dashboardState?.dashboardData,
          data: state?.dashboardState?.dashboardData?.data?.map((item: any) => {
            return {
              ...item,
              options: {
                ...item.options,
                grid: {
                  ...item.options.grid,
                  borderColor: payload === "dark" ? "#A3A5A6" : "#dcdce0",
                },
              },
            };
          }),
        },
      };
    },
    handleAllComponentsLoader: (state, { payload }) => {
      state.allComponentsLoader = payload;
    },
    setDashboardState: (state, { payload }) => {
      state.updateLoader = false;
      state.dashboardState = payload;
    },
    handleAnnotations: (state: any, { payload }) => {
      const severityLevels = [
        {
          color: "#98E565",
          index: 1,
          order: 1,
        },
        {
          color: "#FFEF5C",
          index: 2,
          order: 2,
        },
        {
          color: "#FF8F66",
          index: 3,
          order: 3,
        },
        {
          color: "#871014",
          index: 4,
          order: 4,
        },
        {
          color: "#000000",
          index: 5,
          order: 5,
        },
      ];

      const updatedPayload = payload.map((item: any) => {
        const { ranges, ...rest } = item; // Destructure the 'ranges' key and the rest of the object
        const updatedRanges = ranges.slice(1); // Remove the first element from the 'ranges' array
        return { ...rest, ranges: updatedRanges }; // Create a new object with updated 'ranges' array
      });

      const dashboard = state.chartData?.grafana_data?.find(
        (item: any) => item.id == state.dashboardState?.id
      );

      const res: any = [...dashboard?.dashboardData?.data].map((item: any) => {
        const chart = updatedPayload.find(
          (substance: any) => substance.substanceKey == item.name[0]
        );
        if (chart?.ranges) {
          return {
            ...item,
            options: {
              ...item.options,
              annotations: {
                yaxis: chart?.ranges?.map((range: any) => {
                  const colorInfo: any = severityLevels?.find(
                    (level: any) => level.index == range.index
                  );
                  if (colorInfo) {
                    const { color } = colorInfo;
                    return {
                      y: range.lower,
                      y2: range.upper,
                      borderColor: color,
                      fillColor: color,
                    };
                  } else {
                    return {
                      y: range.lower,
                      y2: range.upper,
                      borderColor: "",
                      fillColor: "",
                    };
                  }
                }),
              },
            },
          };
        } else {
          return { ...item };
        }
      });
    },
    handleDeviceColors: (state: any, { payload }) => {
      const colorArr = [
        "#b702d4",
        "#008fd4",
        "#dbf705",
        "#00ff1a",
        "#0000ff",
        "#ff0008",
        "#ff00e1",
        "#9202f5",
        "#00bfa0",
        "#991f17",
        "#00ffff",
        "#f54b02",
      ];
      state.deviceColors = payload.map((object: any, index: any) => {
        return {
          name: object?.attributes?.name,
          color: colorArr[index % colorArr.length],
        };
      });
    },
    handleUpdateLoader: (state: any, { payload }) => {
      state.updateLoader = payload;
    },
    handleDateTime: (state: any, { payload }) => {
      state.dateTime = { ...payload };
    },
    setFullViewChartData: (state: any, { payload }) => {
      state.fullViewChartData = payload;
    },
    setUserGrafanaDashboardState: (state: any, { payload }) => {
      state.userGrafanaDashboard = payload;
    },
  },
  extraReducers: (builder: any) => {
    builder.addCase(
      getGrafanaDataThunk.fulfilled,
      (state: any, action: any) => {
        state.chartData = action.payload;
        if (!action.payload?.grafana_data) {
          state.dashboardState = {};
        }
      }
    );
    builder.addCase(
      dashBordDetailsQuery.fulfilled,
      (state: any, action: any) => {}
    );
  },
});

export const {
  addLiveChartData,
  handleLoading,
  addChartReadings,
  updateChart,
  handleComponentLoading,
  handleAllComponentsLoader,
  setDashboardState,
  handleThemeChange,
  handleAnnotations,
  handleDeviceColors,
  handleUpdateLoader,
  handleDateTime,
  setFullViewChartData,
  setUserGrafanaDashboardState,
} = grafanaChartsSlice.actions;
export default grafanaChartsSlice.reducer;
